import { FetchPolicy, gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useMemo } from "react";
import { IdentificationFragment } from "./fragment/identificationFragments";
import { UpdateUserPayload, User } from "../models/user";
import { UserFragment } from "./fragment/userFragments";
import { useLoadingState } from "../hooks/useLoading";

export const GET_USER = gql`
  query($id: ID) {
    user(id: $id) {
      ...userFragment
      identifications {
        ...identificationFragment
      }
    }
  }
  ${IdentificationFragment}
  ${UserFragment}
`;

const defaultUser = { organization: { configuration: { allowedOrigins: [] } }} as unknown as User;

export function useUser(
  id = null,
  fetchPolicy: FetchPolicy = "cache-first",
) {
  const { data, error, loading } = useQuery<{ user: User | null }>(GET_USER, {
    variables: { id },
    fetchPolicy,
    notifyOnNetworkStatusChange: true,
  });
  const { clearBusy, setBusy, pushMessageQueue } = useLoadingState();

  useEffect(() => {
    loading ? setBusy("userLoading") : clearBusy("userLoading");

    // Clear busy flag on unmount to avoid infinite spinner
    return () => clearBusy("userLoading");
  }, [loading, setBusy, clearBusy]);

  useEffect(() => {
    if (error) {
      pushMessageQueue({ type: "error", message: error?.message ?? error });
    }
  }, [error, pushMessageQueue]);

  const user = useMemo(() => data?.user ?? defaultUser, [data]);

  return { user, error, loading };
}

// --------------------------------------------------------
export const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      ...userFragment
    }
  }
  ${UserFragment}
`;

export function useUpdateUser() {
  // returns [updateUser, { data, loading, error }]
  return useMutation<{
    updateUser: UpdateUserPayload,
  }>(UPDATE_USER, {
    refetchQueries: [
      GET_USER,
    ],
  });
}
